<template>
  <div class="d-flex flex-column h-100">
    <div class="manipulation-area py-2 pl-3 pr-0">
      <b-row>
        <b-col class="ai-texter-search">
          <CommonFilter v-model="viewModel.searchTerm" :placeholder="$t('AiTexter.FilterByAttributeName')" prepend-icon="Filter" trigger-by-time></CommonFilter>
        </b-col>
      </b-row>
    </div>
    <div class="ai-texter-table mt-2" v-if="sortedList.length > 0">
      <div class="ai-texter-table-header">
        <div class="ai-texter-table-col options-column pr-0">
          <b-checkbox v-model="viewModel.selectAll" @change="toggleSelectAll" :indeterminate="isIndeterminate" />
        </div>
        <div class="ai-texter-table-col value-col pl-0" :class="{ 'sorted-by': viewModel.sortBy.name == 'name' }" @click="setSortBy('name')">
          <div class="pl-2">{{ $t("AiTexter.Attribute") }}</div>
          <div class="ml-2" v-if="viewModel.sortBy.name == 'name'">
            <Icon icon="ChevronBottom" class="font-weight-bolder" v-if="!viewModel.sortBy.desc" />
            <Icon icon="ChevronTop" class="font-weight-bolder" v-if="viewModel.sortBy.desc" />
          </div>
        </div>
        <div class="ai-texter-table-col mid-col pl-0" :class="{ 'sorted-by': viewModel.sortBy.name == 'value' }" @click="setSortBy('value')">
          <div class="pl-2">{{ $t("AiTexter.Value") }}</div>
          <div class="ml-2" v-if="viewModel.sortBy.name == 'value'">
            <Icon icon="ChevronBottom" class="font-weight-bolder" v-if="!viewModel.sortBy.desc" />
            <Icon icon="ChevronTop" class="font-weight-bolder" v-if="viewModel.sortBy.desc" />
          </div>
        </div>
        <div class="ai-texter-table-col small-col pl-0" :class="{ 'sorted-by': viewModel.sortBy.name == 'userType' }" @click="setSortBy('userType')">
          <div class="pl-2">{{ $t("AiTexter.AttributeType") }}</div>
          <div class="ml-2" v-if="viewModel.sortBy.name == 'userType'">
            <Icon icon="ChevronBottom" class="font-weight-bolder" v-if="!viewModel.sortBy.desc" />
            <Icon icon="ChevronTop" class="font-weight-bolder" v-if="viewModel.sortBy.desc" />
          </div>
        </div>
      </div>
      <div class="ai-texter-table-overflow-container">
        <div v-for="sortedItem in sortedList" :key="sortedItem.attributeId" class="ai-texter-table-row">
          <div class="ai-texter-table-row-container">
            <div class="ai-texter-table-row-data">
              <div class="ai-texter-table-col options-column pr-0">
                <b-checkbox v-model="sortedItem.selected" @change="(e) => handleCheckboxChange(e, sortedItem)" />
              </div>
              <div class="ai-texter-table-col value-col pl-0" @click="sortedItem.selected = !sortedItem.selected">
                <span class="pl-2">{{ `${sortedItem.erpSectionName}/${sortedItem.erpAttributeName}` }}</span>
              </div>
              <div class="ai-texter-table-col mid-col pl-0" @click="sortedItem.selected = !sortedItem.selected">
                <span class="pl-2">{{ `${sortedItem.erpAttributeValue} ${sortedItem.erpAttributeUnit}` }}</span>
              </div>
              <div class="ai-texter-table-col small-col pl-0" @click="sortedItem.selected = !sortedItem.selected">
                <span class="pl-2">
                  {{ sortedItem.erpAttributeType == "" ? `<${$t("none")}>` : sortedItem.erpAttributeType }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <Alert with-icon show variant="info">
        {{ $t("AiTexter.NoERPAttributeAvailable") }}
      </Alert>
    </div>
  </div>
</template>

<script setup lang="ts">
import Alert from "@common/components/Alert.vue";
import CommonFilter from "@common/components/CommonFilter.vue";
import Icon from "@common/components/Icon.vue";
import { SortedTypes } from "@medapro-types/Exports/Exports";
import _ from "lodash";
import { computed, reactive, ref } from "vue";
const emits = defineEmits(["update:selectedItems"]);

//#region General
const props = defineProps<{ items: any }>();
//#endregion

type ViewModel = {
  attributes: any;
  selectedErpAttributes: any;
  sortBy: { name: SortedTypes; desc: boolean };
  selectAll: any;
  searchTerm: string;
  inputIsFocused: boolean;
};

const viewModel = reactive<ViewModel>({
  attributes: {},
  selectedErpAttributes: [],
  sortBy: { name: "", desc: false },
  selectAll: ref(false),
  searchTerm: "",
  inputIsFocused: false
});

const sortedList = computed(() => {
  const filter = viewModel.searchTerm ? viewModel.searchTerm.toLowerCase() : null;
  const filteredItems = props.items?.filter((item) => item.erpAttributeValue && item.erpAttributeValue.trim() !== "");
  const source = !filter ? filteredItems : _.filter(filteredItems, (x) => _.includes(x.erpSectionName.toLowerCase(), viewModel.searchTerm.toLowerCase()));
  const sorted = _.sortBy(source, viewModel.sortBy.name);
  return viewModel.sortBy.desc ? sorted.reverse() : sorted;
});

const setSortBy = (sortByName: SortedTypes) => {
  if (viewModel.sortBy.name != sortByName) {
    viewModel.sortBy = { name: sortByName, desc: false };
  } else {
    viewModel.sortBy.desc = !viewModel.sortBy.desc;
  }
};

const toggleSelectAll = () => {
  const isSelected = viewModel.selectAll;
  props.items.forEach((item) => (item.selected = isSelected));
  viewModel.selectedErpAttributes = viewModel.selectAll ? props.items.filter((item) => item.erpAttributeValue !== "") : [];
  emits("update:selectedItems", viewModel.selectedErpAttributes);
};

const isIndeterminate = computed(() => {
  const selectedCount = sortedList.value.filter((item: any) => item.selected).length;
  return selectedCount > 0 && selectedCount < sortedList.value.length;
});

function handleCheckboxChange(e: any, parameter: any) {
  if (e) {
    viewModel.selectedErpAttributes.push(parameter);
  } else {
    viewModel.selectedErpAttributes = viewModel.selectedErpAttributes.filter((item: any) => item.attributeId !== parameter.attributeId);
  }
  const selectedCount = sortedList.value.filter((item: any) => item.selected).length;
  viewModel.selectAll = selectedCount === sortedList.value.length;
  emits("update:selectedItems", viewModel.selectedErpAttributes);
}
</script>
